import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToastContext } from '../shared/context/ToastContext';
import { useAuth } from '../shared/context/AuthContext';
import './CreateCampaign.css';
import { apiFetch } from '../shared/api';

function CreateCampaign() {
  const navigate = useNavigate();
  const { success, error: showError } = useToastContext();
  const { isAdmin, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [brands, setBrands] = useState([]);
  const [feedDomains, setFeedDomains] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    link: '',
    brand: '',
    custom_label_1: '',
    custom_label_2: '',
    custom_label_3: '',
    client_id: '',
    feed_domain_id: ''
  });

  React.useEffect(() => {
    (async () => {
      try {
        if (isAdmin) {
          const response = await apiFetch('/clients');
          if (!response.ok) return;
          const data = await response.json();
          setClients(data);
          if (!formData.client_id && data.length > 0) {
            const preferred = data.find((client) => client.id === 'unassigned')?.id || data[0].id;
            setFormData((prev) => ({ ...prev, client_id: preferred }));
          }
          return;
        }
        setFormData((prev) => ({ ...prev, client_id: user?.primary_client_id || '' }));
      } catch {
        // noop
      }
    })();
  }, [isAdmin, user?.primary_client_id]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    (async () => {
      try {
        const clientId = isAdmin ? formData.client_id : user?.primary_client_id;
        if (!clientId) {
          setBrands([]);
          setFormData((prev) => ({ ...prev, brand: '' }));
          return;
        }
        const response = await apiFetch(`/brands?client_id=${encodeURIComponent(clientId)}`);
        if (!response.ok) return;
        const data = await response.json();
        setBrands(data);
        if (data.length > 0 && !data.includes(formData.brand)) {
          setFormData((prev) => ({ ...prev, brand: data[0] }));
        }
      } catch {
        // noop
      }
    })();
  }, [isAdmin, formData.client_id, user?.primary_client_id]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    (async () => {
      try {
        const clientId = isAdmin ? formData.client_id : user?.primary_client_id;
        if (!clientId) {
          setFeedDomains([]);
          setFormData((prev) => ({ ...prev, feed_domain_id: '' }));
          return;
        }
        const response = await apiFetch(`/feed-domains?client_id=${encodeURIComponent(clientId)}`);
        if (!response.ok) return;
        const data = await response.json();
        setFeedDomains(data);
        if (!data.some((domain) => domain.id === formData.feed_domain_id)) {
          setFormData((prev) => ({ ...prev, feed_domain_id: '' }));
        }
      } catch {
        // noop
      }
    })();
  }, [isAdmin, formData.client_id, user?.primary_client_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
      ...(name === 'client_id' ? { brand: '', feed_domain_id: '' } : {})
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        link: formData.link,
        brand: formData.brand,
        client_id: formData.client_id,
        feed_domain_id: formData.feed_domain_id || null,
        default_label_1_headline: formData.custom_label_1 || '',
        default_label_2_description: formData.custom_label_2 || '',
        default_primary_description: formData.custom_label_3 || ''
      };
      const response = await apiFetch('/campaigns', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create campaign');
      }

      const campaign = await response.json();
      success('Campaign created successfully!');
      navigate(`/campaigns/${campaign.id}`);
    } catch (err) {
      showError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="create-campaign">
      <div className="create-campaign-header">
        <h2>Create New Campaign</h2>
        <button onClick={() => navigate('/')} className="btn btn-secondary">
          ← Home
        </button>
      </div>

      <form onSubmit={handleSubmit} className="campaign-form">
        <div className="form-group campaign-name-group">
          <label htmlFor="name">Campaign Name *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder="e.g., Summer Collection 2024"
          />
        </div>

        <div className="form-section">
          <h3>Campaign Setup</h3>

          <p className="form-help">
            Campaign Brand and Description are local notes for your own campaign organization.
            Primary Description and Custom Labels below are defaults inherited by all creatives (and can be edited per creative).
          </p>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="description">Description (Local Campaign Notes)</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Catalogue Description"
              />
            </div>
            <div className="form-group">
              <label htmlFor="brand">Brand</label>
              <select
                id="brand"
                name="brand"
                value={formData.brand}
                onChange={handleChange}
                required
              >
                {formData.brand && !brands.includes(formData.brand) && (
                  <option value={formData.brand}>{formData.brand}</option>
                )}
                {brands.length === 0 ? (
                  <option value="">No brands configured</option>
                ) : (
                  brands.map((brand) => (
                    <option key={brand} value={brand}>{brand}</option>
                  ))
                )}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="custom_label_3">Description (Primary)</label>
            <textarea
              id="custom_label_3"
              name="custom_label_3"
              value={formData.custom_label_3}
              onChange={handleChange}
              placeholder="Primary description default"
            />
          </div>

          <div className="form-group">
            <label htmlFor="custom_label_1">Custom Label [1] (Headline)</label>
            <input
              type="text"
              id="custom_label_1"
              name="custom_label_1"
              value={formData.custom_label_1}
              onChange={handleChange}
              placeholder="Headline default"
            />
          </div>

          <div className="form-group">
            <label htmlFor="custom_label_2">Custom Label [2] (Description)</label>
            <input
              type="text"
              id="custom_label_2"
              name="custom_label_2"
              value={formData.custom_label_2}
              onChange={handleChange}
              placeholder="Description default"
            />
          </div>

          <div className="form-group">
            <label htmlFor="link">Product Link *</label>
            <input
              type="url"
              id="link"
              name="link"
              value={formData.link}
              onChange={handleChange}
              required
              placeholder="https://example.com/product"
            />
          </div>

          {isAdmin && (
            <div className="form-group">
              <label htmlFor="client_id">Client (Admin only)</label>
              <select
                id="client_id"
                name="client_id"
                value={formData.client_id}
                onChange={handleChange}
              >
                <option value="unassigned">Unassigned</option>
                {clients.filter((client) => client.id !== 'unassigned').map((client) => (
                  <option key={client.id} value={client.id}>{client.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="feed_domain_id">Feed Domain (optional)</label>
            <select
              id="feed_domain_id"
              name="feed_domain_id"
              value={formData.feed_domain_id}
              onChange={handleChange}
            >
              <option value="">Use current API host</option>
              {feedDomains.map((domain) => (
                <option key={domain.id} value={domain.id}>{domain.host}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-actions">
          <button type="button" onClick={() => navigate('/')} className="btn btn-secondary">
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Creating...' : 'Create Campaign'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CreateCampaign;

